/**
 * Shift management view — Phase 2.3.k.2.
 *
 * Owns the customer-care shift lifecycle: top-bar banner, shift setup
 * dialog (assigned lines + PBX trunks), shift close dialog (handover
 * candidate + open-tickets picker), shift end alert (countdown + auto-
 * logout), and shift status polling (60s interval).
 *
 * Shares state and entry points with auth/login/boot which still live in
 * admin.js — `currentShiftInfo`, `shiftPromptDismissed`,
 * `ensureShiftPrompt` / `startShiftStatusPolling` / `stopShiftStatusPolling`
 * are exposed via `views.shiftManagement.*`.
 */

// Phase 3.2.c: replaced `configure({helpers})` injection with direct imports.
// `adminUsersCache` was admin.js state shared only with this view — moved here.
// `getRegisteredPbxTrunks` reads ADMIN_ME + the in-view shift state, so it's
// inlined here using the auth getter from src/lib/roles.js.
import { escapeHtml, formatDateTimeShort, formatRelativeTime, parseListInput, normalizeStringList, getFormattingConfig } from '../../src/lib/format.js';
import { openDialog } from '../../src/lib/dialog.js';
import { hasRole, canAccessTicketing, getCurrentAdminUser } from '../../src/lib/roles.js';
import { normalizePbxConfig, isValidTimeString } from '../../src/lib/pbx.js';
import { performLogout } from '../../src/lib/auth.js';
import { formatTicketStatus, formatTicketPriority } from '../../src/lib/ticket-utils.js';
import { ensureTechnicianUsersLoaded, getTechnicianAdminUsers } from '../../src/lib/admin-roster.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

  const adminAPI = api;
  const API = api.routes;
  const showFlash = (msg, type, ms) => notify.showFlash(msg, type, ms);

  // Cross-view lookups — both targets are sibling view modules.
  const getCurrentTechnicianAssignment = (ticket) => {
    const t = window.adminViews && window.adminViews.technicians;
    return t ? t.findTechnicianByAdminUserId(ticket && ticket.assignee_user_id) : null;
  };
  const loadActiveShifts = () => {
    const hr = window.adminViews && window.adminViews.hr;
    return hr && hr.loadActiveShifts ? hr.loadActiveShifts() : Promise.resolve();
  };
  const loadShiftTemplates = () => {
    const hr = window.adminViews && window.adminViews.hr;
    return hr && hr.loadShiftTemplates ? hr.loadShiftTemplates() : Promise.resolve();
  };
  const reloadTickets = (opts) => {
    const t = window.adminViews && window.adminViews.tickets;
    return t && typeof t.reloadTickets === 'function' ? t.reloadTickets(opts) : Promise.resolve();
  };

  // Verbatim function bodies reference `ADMIN_ME` — bridge to the live
  // user object via the auth-module getter.
  const ADMIN_ME = new Proxy({}, {
    get(_, prop) { const u = getCurrentAdminUser(); return u ? u[prop] : undefined; },
    has(_, prop) { const u = getCurrentAdminUser(); return u && (prop in u); },
  });

  // adminUsersCache used to live in admin.js but is now owned here (only
  // read/written by this view).
  let adminUsersCache = null;
  const getAdminUsersCache = () => adminUsersCache;
  const setAdminUsersCache = (v) => { adminUsersCache = v; };

  // PBX trunks registry: prefer the live shift's trunks, fall back to the
  // current admin user's profile defaults. `currentShiftInfo` is the
  // in-view state declared further down in this file; the reference here
  // is resolved at call time (after that declaration has executed).
  function getRegisteredPbxTrunks() {
    const fromShift = normalizeStringList(currentShiftInfo && currentShiftInfo.pbx_trunks, { lowercase: true });
    if (fromShift.length) return fromShift;
    const fromProfile = normalizeStringList(ADMIN_ME && ADMIN_ME.pbx_trunks, { lowercase: true });
    return fromProfile;
  }

  // ---- DOM refs (lazy-resolved) ---------------------------------------
  let shiftSetupDlg, shiftSetupForm, shiftLinesInput, shiftLinesSuggestion, shiftTrunksSelect, shiftSetupStatus, shiftSetupSubmit, shiftSetupSkip, shiftCloseBtn, shiftCloseDlg, shiftCloseForm, shiftCloseNextUser, shiftCloseTickets, shiftCloseNotes, shiftCloseStatus, shiftCloseSubmit, shiftCloseCancel, shiftEndAlert, shiftEndAlertMessage, shiftEndAlertCountdown, shiftEndAlertConfirm,
      shiftBanner, shiftSetupBtn;

  let domResolved = false;
  function resolveDom() {
    if (domResolved) return;
    domResolved = true;
    shiftSetupDlg = document.getElementById('shiftSetupDlg');
    shiftSetupForm = document.getElementById('shiftSetupForm');
    shiftLinesInput = document.getElementById('shiftLinesInput');
    shiftLinesSuggestion = document.getElementById('shiftLinesSuggestion');
    shiftTrunksSelect = document.getElementById('shiftTrunksSelect');
    shiftSetupStatus = document.getElementById('shiftSetupStatus');
    shiftSetupSubmit = document.getElementById('shiftSetupSubmit');
    shiftSetupSkip = document.getElementById('shiftSetupSkip');
    shiftCloseBtn = document.getElementById('shiftCloseBtn');
    shiftCloseDlg = document.getElementById('shiftCloseDlg');
    shiftCloseForm = document.getElementById('shiftCloseForm');
    shiftCloseNextUser = document.getElementById('shiftCloseNextUser');
    shiftCloseTickets = document.getElementById('shiftCloseTickets');
    shiftCloseNotes = document.getElementById('shiftCloseNotes');
    shiftCloseStatus = document.getElementById('shiftCloseStatus');
    shiftCloseSubmit = document.getElementById('shiftCloseSubmit');
    shiftCloseCancel = document.getElementById('shiftCloseCancel');
    shiftEndAlert = document.getElementById('shiftEndAlert');
    shiftEndAlertMessage = document.getElementById('shiftEndAlertMessage');
    shiftEndAlertCountdown = document.getElementById('shiftEndAlertCountdown');
    shiftEndAlertConfirm = document.getElementById('shiftEndAlertConfirm');
    shiftBanner = document.getElementById('shiftBanner');
    shiftSetupBtn = document.getElementById('shiftSetupBtn');
  }

  // ---- State (verbatim from admin.js) ---------------------------------
    let shiftPromptDismissed = false;
    let shiftOptionsCache = null;
    let shiftAvailableTrunks = [];
    let shiftCloseState = { tickets: [], loading: false, submitting: false };
    let currentShiftInfo = null;
    const SHIFT_BANNER_STATES = ['shift-banner--active', 'shift-banner--warning', 'shift-banner--inactive'];
    const SHIFT_STATUS_POLL_MS = 60000;
    const SHIFT_END_ALERT_GRACE_MS = 10 * 60 * 1000;
    const SHIFT_END_ALERT_COOLDOWN_MS = 30 * 60 * 1000;
    const shiftEndAlertState = {
      expectedLogoutAt: null,
      nextPromptAt: null,
      autoLogoutTimer: null,
      countdownTimer: null,
      autoLogoutDeadline: null,
      showing: false,
    };
    let shiftEndAutoClosing = false;
    let shiftStatusTimer = null;

    const SHIFT_REGISTRANT_ROLES = ['customer_care'];
    const SHIFT_HANDOVER_CANDIDATE_ROLES = ['customer_care', 'customer_care_supervisor', 'billing_officer', 'billing_supervisor', 'root'];
    const SHIFT_CLOSE_OPEN_STATUSES = ['pending', 'in_progress', 'waiting_customer', 'escalated', 'parked'];
    const SHIFT_DEFAULT_LINE_BY_ROLE = {
      customer_care: ['support'],
      customer_care_supervisor: ['support'],
      billing_officer: ['billing'],
      billing_supervisor: ['billing'],
    };
    const STAFF_GROUP_LABELS = {
      customer_care: 'Customer care',
      field_tech: 'Field tech',
      call_center: 'Call center',
      noc: 'NOC',
      security: 'Security',
      regular: 'Regular',
    };
    const WEEKDAY_LABELS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
;


  // ---- Region A: banner + alert + polling ----------------------
    function formatShiftTime(value) {
      if (!value) return '';
      try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        return date.toLocaleTimeString(getFormattingConfig().locale, { hour: '2-digit', minute: '2-digit', ...(getFormattingConfig().timeZone ? { timeZone: getFormattingConfig().timeZone } : {}) });
      } catch {
        return '';
      }
    }





    function updateShiftBanner(info) {
      if (!shiftBanner) return;
      shiftBanner.classList.remove(...SHIFT_BANNER_STATES);
      if (!info || !info.login_at) {
        currentShiftInfo = null;
        shiftBanner.textContent = '⏳ Shift not started';
        shiftBanner.classList.add('shift-banner--inactive');
        updateShiftControls();
        evaluateShiftEndAlert(null);
        return;
      }
      currentShiftInfo = info;
      const grade =
        info.performance_grade
        || (info.performance_breakdown && info.performance_breakdown.grade)
        || null;
      const hasScore = typeof info.performance_score === 'number' && !Number.isNaN(info.performance_score);
      const roundedScore = hasScore ? Math.round(info.performance_score) : null;
      const scoreLabel = hasScore
        ? `Score: ${roundedScore}${grade ? ` (${grade})` : ''}`
        : 'Score: –';
      if (info.logout_at) {
        const closedPieces = ['✅ Shift closed'];
        if (scoreLabel) closedPieces.push(scoreLabel);
        shiftBanner.textContent = closedPieces.join(' • ');
        shiftBanner.classList.add('shift-banner--inactive');
        updateShiftControls();
        evaluateShiftEndAlert(info);
        return;
      }
      const assigned = normalizeStringList(info.assigned_lines || []);
      const trunks = normalizeStringList(info.pbx_trunks || []);
      const pieces = [];
      if (assigned.length) pieces.push(`Lines: ${assigned.join(', ')}`);
      if (trunks.length) pieces.push(`PBX: ${trunks.join(', ')}`);
      const logoutLabel = formatShiftTime(info.expected_logout_at);
      if (logoutLabel) pieces.push(`Ends ${logoutLabel}`);
      pieces.push(scoreLabel);
      if (!pieces.length) pieces.push('Shift active');
      shiftBanner.textContent = pieces.join(' • ');
      shiftBanner.classList.add(trunks.length ? 'shift-banner--active' : 'shift-banner--warning');
      updateShiftControls();
      evaluateShiftEndAlert(info);
    }

    function updateShiftControls() {
      const shiftAllowed = isShiftRegistrant();
      if (shiftSetupBtn) {
        shiftSetupBtn.dataset.allowed = shiftAllowed ? '1' : '0';
        shiftSetupBtn.style.display = shiftAllowed ? '' : 'none';
      }
      if (shiftCloseBtn) {
        const closable = shiftAllowed && currentShiftInfo && currentShiftInfo.login_at && !currentShiftInfo.logout_at;
        shiftCloseBtn.dataset.allowed = closable ? '1' : '0';
        shiftCloseBtn.style.display = closable ? '' : 'none';
        shiftCloseBtn.disabled = !closable;
      }
      if (shiftSetupSkip) {
        const allowSkip = canSkipShiftPrompt();
        shiftSetupSkip.style.display = allowSkip ? '' : 'none';
        shiftSetupSkip.disabled = !allowSkip;
      }
    }

    function clearShiftEndAlert({ resetSchedule = false } = {}) {
      if (shiftEndAlertState.autoLogoutTimer) {
        clearTimeout(shiftEndAlertState.autoLogoutTimer);
        shiftEndAlertState.autoLogoutTimer = null;
      }
      if (shiftEndAlertState.countdownTimer) {
        clearInterval(shiftEndAlertState.countdownTimer);
        shiftEndAlertState.countdownTimer = null;
      }
      shiftEndAlertState.autoLogoutDeadline = null;
      shiftEndAlertState.showing = false;
      if (shiftEndAlertCountdown) shiftEndAlertCountdown.textContent = '';
      if (shiftEndAlert) shiftEndAlert.hidden = true;
      if (resetSchedule) {
        shiftEndAlertState.expectedLogoutAt = null;
        shiftEndAlertState.nextPromptAt = null;
      }
    }

    function updateShiftEndCountdown() {
      if (!shiftEndAlertCountdown || !shiftEndAlertState.autoLogoutDeadline) return;
      const remaining = Math.max(0, shiftEndAlertState.autoLogoutDeadline - Date.now());
      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      const label = remaining <= 0
        ? 'Logging you out now…'
        : `We will log you out automatically in ${minutes}:${String(seconds).padStart(2, '0')} if you do not respond.`;
      shiftEndAlertCountdown.textContent = label;
    }

    function openShiftEndAlert() {
      if (!shiftEndAlert) return;
      shiftEndAlertState.showing = true;
      shiftEndAlert.hidden = false;
      const endLabel = currentShiftInfo && currentShiftInfo.expected_logout_at
        ? formatDateTimeShort(currentShiftInfo.expected_logout_at)
        : 'your scheduled end time';
      if (shiftEndAlertMessage) {
        shiftEndAlertMessage.textContent = `Your shift ended at ${endLabel}. Please close your shift and logout to complete handover.`;
      }
      shiftEndAlertState.autoLogoutDeadline = Date.now() + SHIFT_END_ALERT_GRACE_MS;
      updateShiftEndCountdown();
      if (shiftEndAlertState.autoLogoutTimer) clearTimeout(shiftEndAlertState.autoLogoutTimer);
      shiftEndAlertState.autoLogoutTimer = setTimeout(() => {
        handleShiftEndAutoLogout();
      }, SHIFT_END_ALERT_GRACE_MS);
      if (shiftEndAlertState.countdownTimer) clearInterval(shiftEndAlertState.countdownTimer);
      shiftEndAlertState.countdownTimer = setInterval(updateShiftEndCountdown, 1000);
      if (shiftEndAlertConfirm) {
        setTimeout(() => {
          try { shiftEndAlertConfirm.focus(); } catch {}
        }, 30);
      }
      shiftEndAlertState.nextPromptAt = Date.now() + SHIFT_END_ALERT_COOLDOWN_MS;
    }

    function acknowledgeShiftEndAlert() {
      if (!shiftEndAlertState.showing) return;
      clearShiftEndAlert();
      shiftEndAlertState.nextPromptAt = Date.now() + SHIFT_END_ALERT_COOLDOWN_MS;
    }

    async function handleShiftEndAutoLogout() {
      if (shiftEndAutoClosing) return;
      shiftEndAutoClosing = true;
      clearShiftEndAlert({ resetSchedule: true });
      const shiftId = currentShiftInfo && currentShiftInfo.id ? currentShiftInfo.id : null;
      try {
        const response = await adminAPI.fetch(API.adminShiftTimeoutClose, {
          method: 'POST',
          body: JSON.stringify({ shift_id: shiftId }),
        });
        if (response) {
          updateShiftBanner(response);
        }
      } catch (err) {
        showFlash(err && err.message ? err.message : 'Failed to auto-close shift; logging out locally.', 'error', 8000);
      } finally {
        performLogout({ skipShiftGuard: true, reason: 'shift_end_timeout' });
        shiftEndAutoClosing = false;
      }
    }

    function evaluateShiftEndAlert(info) {
      const eligible = isShiftRegistrant() && canAccessTicketing();
      if (!eligible) {
        clearShiftEndAlert({ resetSchedule: true });
        return;
      }
      if (!info || !info.login_at || info.logout_at) {
        clearShiftEndAlert({ resetSchedule: true });
        return;
      }
      const expected = Date.parse(info.expected_logout_at);
      if (!Number.isFinite(expected)) {
        clearShiftEndAlert({ resetSchedule: true });
        return;
      }
      if (shiftEndAlertState.expectedLogoutAt !== expected) {
        shiftEndAlertState.expectedLogoutAt = expected;
        shiftEndAlertState.nextPromptAt = expected;
        clearShiftEndAlert();
      }
      const now = Date.now();
      if (now < expected) {
        shiftEndAlertState.nextPromptAt = expected;
        clearShiftEndAlert();
        return;
      }
      const nextPrompt = shiftEndAlertState.nextPromptAt || expected;
      if (now < nextPrompt) {
        return;
      }
      if (!shiftEndAlertState.showing) {
        openShiftEndAlert();
      }
    }

    function stopShiftStatusPolling() {
      if (shiftStatusTimer) {
        clearInterval(shiftStatusTimer);
        shiftStatusTimer = null;
      }
    }

    function startShiftStatusPolling() {
      stopShiftStatusPolling();
      if (!isShiftRegistrant() || !canAccessTicketing()) return;
      refreshShiftStatus({ silent: true }).catch(() => {});
      shiftStatusTimer = setInterval(() => {
        refreshShiftStatus({ silent: true }).catch(() => {});
      }, SHIFT_STATUS_POLL_MS);
    }

    async function refreshShiftStatus({ silent = false } = {}) {
      if (!isShiftRegistrant() || !canAccessTicketing()) {
        clearShiftEndAlert({ resetSchedule: true });
        return null;
      }
      let shiftData = null;
      try {
        shiftData = await adminAPI.fetch(API.adminShiftToday, { method: 'GET' });
      } catch (err) {
        if (err && err.status === 404) {
          updateShiftBanner(null);
          return null;
        }
        if (!silent) {
          showFlash(err && err.message ? err.message : 'Failed to refresh shift status.', 'error');
        } else {
          console.warn('shift_status_refresh_failed', err);
        }
        return null;
      }
      updateShiftBanner(shiftData && shiftData.login_at ? shiftData : null);
      return shiftData;
    }

  // ---- Region B: role helpers ----------------------------------
    function isShiftRegistrant() {
      if (!hasRole(...SHIFT_REGISTRANT_ROLES)) return false;
      if (hasRole('customer_care_supervisor')) return false;
      return true;
    }

    function canSkipShiftPrompt() {
      return !hasRole('customer_care');
    }

    // Restored in the rework cleanup: the per-role default assigned lines for
    // the current user, deduped. Roles may arrive as strings or { name }
    // objects (same shapes `hasRole` handles), so normalize both.
    function getDefaultAssignedLinesForCurrentUser() {
      const rawRoles = Array.isArray(ADMIN_ME?.roles) ? ADMIN_ME.roles : [];
      const roles = rawRoles.map((r) => (r && typeof r === 'object' && r.name ? String(r.name) : String(r)));
      if (!roles.length) return [];
      const collected = [];
      const seen = new Set();
      roles.forEach((role) => {
        const defaults = SHIFT_DEFAULT_LINE_BY_ROLE[role.toLowerCase()];
        if (!defaults) return;
        defaults.forEach((line) => {
          const value = String(line || '').trim();
          if (!value) return;
          const dedupeKey = value.toLowerCase();
          if (seen.has(dedupeKey)) return;
          seen.add(dedupeKey);
          collected.push(value);
        });
      });
      return collected;
    }

  // ---- Region C: dialogs + handlers ----------------------------
    function setShiftStatus(message, tone = 'error') {
      if (!shiftSetupStatus) return;
      if (!message) {
        shiftSetupStatus.textContent = '';
        shiftSetupStatus.style.color = 'var(--sub)';
        return;
      }
      shiftSetupStatus.textContent = message;
      if (tone === 'success') {
        shiftSetupStatus.style.color = 'var(--success, #22c55e)';
      } else if (tone === 'info') {
        shiftSetupStatus.style.color = 'var(--sub)';
      } else {
        shiftSetupStatus.style.color = 'var(--danger, #ef4444)';
      }
    }

    function setShiftCloseStatus(message, tone = 'info') {
      if (!shiftCloseStatus) return;
      if (!message) {
        shiftCloseStatus.textContent = '';
        shiftCloseStatus.className = 'dlg-status';
        return;
      }
      shiftCloseStatus.textContent = message;
      const base = ['dlg-status'];
      if (tone === 'success') base.push('success');
      else if (tone === 'error') base.push('error');
      shiftCloseStatus.className = base.join(' ');
    }

    // `setPendingEnquiryStatus` lives in `frontend/admin/js/views/tickets.js` (Phase 2.1.b.3-detail).

    function populateTrunkSelect(available, selected) {
      if (!shiftTrunksSelect) return;
      const normalizedAvailable = normalizeStringList(available || []);
      const normalizedSelected = normalizeStringList(selected || []);
      const selectedLookup = new Set(normalizedSelected.map((value) => value.toLowerCase()));
      const seen = new Set();
      const options = [];

      normalizedAvailable.forEach((value) => {
        const key = value.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        options.push({ value, label: value, selected: selectedLookup.has(key), missing: false });
      });

      normalizedSelected.forEach((value) => {
        const key = value.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        options.push({ value, label: `${value} (not in list)`, selected: true, missing: true });
      });

      shiftTrunksSelect.innerHTML = '';
      options.forEach((entry) => {
        const opt = document.createElement('option');
        opt.value = entry.value;
        opt.textContent = entry.label;
        if (entry.selected) opt.selected = true;
        if (entry.missing) opt.dataset.missing = '1';
        shiftTrunksSelect.appendChild(opt);
      });
      const displayCount = Math.max(options.length, 3);
      shiftTrunksSelect.size = Math.min(displayCount, 10);
      const hasSelectableTrunks = normalizedAvailable.length > 0 || normalizedSelected.length > 0;
      shiftTrunksSelect.disabled = !hasSelectableTrunks;
      if (shiftSetupSubmit) {
        shiftSetupSubmit.disabled = !hasSelectableTrunks;
      }
    }

    function openShiftDialog({
      assignedLines = [],
      pbxTrunks = [],
      defaultLines = [],
      availableTrunks = [],
      reason = 'missing_trunk',
    } = {}) {
      shiftAvailableTrunks = Array.isArray(availableTrunks) ? [...availableTrunks] : [];
      const linesToUse = assignedLines.length ? assignedLines : defaultLines;
      if (shiftLinesInput) shiftLinesInput.value = linesToUse.join(', ');
      if (shiftLinesSuggestion) {
        if (defaultLines.length) {
          shiftLinesSuggestion.textContent = `Assigned lines based on your role: ${defaultLines.join(', ')}`;
          shiftLinesSuggestion.style.display = '';
        } else {
          shiftLinesSuggestion.textContent = '';
          shiftLinesSuggestion.style.display = 'none';
        }
      }
      populateTrunkSelect(shiftAvailableTrunks, pbxTrunks);
      if (shiftSetupSkip) {
        const allowSkip = canSkipShiftPrompt();
        shiftSetupSkip.style.display = allowSkip ? '' : 'none';
        shiftSetupSkip.disabled = !allowSkip;
      }

      let statusMessage = '';
      let tone = 'info';
      if (!shiftAvailableTrunks.length) {
        statusMessage = 'No PBX trunks are configured. Contact your supervisor.';
        tone = 'error';
      } else if (!pbxTrunks.length) {
        statusMessage = reason === 'no_shift'
          ? 'Start your shift by selecting the PBX trunk(s) you are covering.'
          : 'Select the PBX trunk(s) you are covering this shift.';
      }
      setShiftStatus(statusMessage, tone);
      openDialog(shiftSetupDlg);
      if (shiftTrunksSelect && shiftAvailableTrunks.length) {
        setTimeout(() => {
          try { shiftTrunksSelect.focus(); } catch {}
        }, 60);
      }
    }

    async function loadShiftOptions(force = false) {
      if (!force && shiftOptionsCache) return shiftOptionsCache;
      const data = await adminAPI.fetch(API.adminShiftOptions, { method: 'GET' });
      const normalized = {
        available_trunks: normalizeStringList(data && data.available_trunks),
      };
      shiftOptionsCache = normalized;
      return normalized;
    }

    function getShiftCloseCandidates() {
      const technicianAdminUsers = getTechnicianAdminUsers();
      if (!Array.isArray(technicianAdminUsers)) return [];
      const currentId = ADMIN_ME && typeof ADMIN_ME.id !== 'undefined' ? Number(ADMIN_ME.id) : null;
      return technicianAdminUsers
        .filter((user) => {
          if (!user || typeof user.id === 'undefined') return false;
          if (currentId !== null && Number(user.id) === currentId) return false;
          const roles = Array.isArray(user.roles) ? user.roles.map((role) => String(role).toLowerCase()) : [];
          return roles.some((role) => SHIFT_HANDOVER_CANDIDATE_ROLES.includes(role));
        })
        .map((user) => ({
          id: user.id,
          username: user.username,
          email: user.email,
        }))
        .sort((a, b) => {
          const labelA = (a.username || a.email || `User ${a.id}`).toLowerCase();
          const labelB = (b.username || b.email || `User ${b.id}`).toLowerCase();
          return labelA.localeCompare(labelB);
        });
    }

    function populateShiftCloseCandidates() {
      if (!shiftCloseNextUser) return [];
      const candidates = getShiftCloseCandidates();
      shiftCloseNextUser.innerHTML = '';
      if (!candidates.length) {
        const opt = document.createElement('option');
        opt.value = '';
        opt.textContent = 'No teammate available';
        opt.disabled = true;
        shiftCloseNextUser.appendChild(opt);
        shiftCloseNextUser.disabled = true;
        return [];
      }
      candidates.forEach((candidate, index) => {
        const option = document.createElement('option');
        option.value = String(candidate.id);
        const label = candidate.username || candidate.email || `User #${candidate.id}`;
        option.textContent = label;
        if (index === 0) option.selected = true;
        shiftCloseNextUser.appendChild(option);
      });
      shiftCloseNextUser.disabled = false;
      return candidates;
    }

    function renderShiftCloseTickets(tickets = []) {
      if (!shiftCloseTickets) return;
      shiftCloseTickets.innerHTML = '';
      if (!Array.isArray(tickets) || !tickets.length) {
        const empty = document.createElement('div');
        empty.className = 'shift-close-ticket-empty';
        empty.textContent = 'No open tickets assigned to you.';
        shiftCloseTickets.appendChild(empty);
        return;
      }
      const list = document.createElement('ul');
      list.className = 'shift-close-ticket-items';
      tickets.forEach((ticket) => {
        const li = document.createElement('li');
        const meta = ticket?.meta && typeof ticket.meta === 'object' ? ticket.meta : {};
        const shortCode = meta.ticket_short_code || (ticket.id || '').toString().split('-')[0];
        const status = (ticket?.status || '').toString().replace(/_/g, ' ');
        const subject = ticket?.subject ? ticket.subject : '(no subject)';
        li.innerHTML = `<strong>${escapeHtml(shortCode || '')}</strong> · ${escapeHtml(status)} · ${escapeHtml(subject)}`;
        list.appendChild(li);
      });
      shiftCloseTickets.appendChild(list);
    }

    async function fetchShiftCloseTickets() {
      if (!ADMIN_ME || typeof ADMIN_ME.id === 'undefined') return [];
      const params = new URLSearchParams();
      params.set('assignee_user_id', String(ADMIN_ME.id));
      params.set('limit', '200'); // backend caps `limit` at le=200
      SHIFT_CLOSE_OPEN_STATUSES.forEach((status) => params.append('status', status));
      const data = await adminAPI.fetch(`${API.adminTickets}?${params.toString()}`, { method: 'GET' });
      const items = Array.isArray(data?.items) ? data.items : [];
      return items;
    }

    async function openShiftCloseDialog(options = {}) {
      if (!shiftCloseDlg) {
        // eslint-disable-next-line no-console
        console.warn('shift-management: #shiftCloseDlg missing — close-shift button has no effect.');
        return;
      }
      if (!isShiftRegistrant()) {
        showFlash('Shift handover is only available to active agents.', 'error');
        return;
      }
      if (!ADMIN_ME || typeof ADMIN_ME.id === 'undefined') {
        showFlash('Your profile is still loading. Please try again.', 'error');
        return;
      }

      // When the dialog is opened from the logout guard, finish the logout
      // automatically once the handover is saved (the user already asked to
      // log out). The standalone "Close shift" button leaves this false.
      shiftCloseState.logoutAfter = Boolean(options.logoutAfter);
      setShiftCloseStatus('');
      shiftCloseState.tickets = [];
      if (shiftCloseNotes) shiftCloseNotes.value = '';
      renderShiftCloseTickets([]);
      if (shiftCloseSubmit) shiftCloseSubmit.disabled = true;

      let shiftData = null;
      try {
        shiftData = await adminAPI.fetch(API.adminShiftToday, { method: 'GET' });
      } catch (err) {
        if (err && err.status === 404) {
          setShiftCloseStatus('You have not started a shift for today.', 'error');
        } else {
          setShiftCloseStatus(err && err.message ? err.message : 'Failed to load shift information.', 'error');
        }
        return;
      }

      if (!shiftData || !shiftData.login_at) {
        setShiftCloseStatus('You have not started a shift for today.', 'error');
        return;
      }
      if (shiftData.logout_at) {
        updateShiftBanner(shiftData);
        setShiftCloseStatus('Your shift is already closed.', 'info');
        return;
      }

      openDialog(shiftCloseDlg);

      setShiftCloseStatus('Loading teammates…', 'info');

      try {
        await ensureTechnicianUsersLoaded();
      } catch (err) {
        setShiftCloseStatus(err && err.message ? err.message : 'Failed to load teammate list.', 'error');
        return;
      }

      const candidates = populateShiftCloseCandidates();
      if (!candidates.length) {
        setShiftCloseStatus('No teammate with shift access is currently available for handover. Contact your supervisor.', 'error');
      } else {
        setShiftCloseStatus('Select the teammate taking over and confirm the handover.', 'info');
      }

      if (shiftCloseTickets) {
        shiftCloseTickets.innerHTML = '<div class="shift-close-ticket-loading">Loading open tickets…</div>';
      }

      try {
        const tickets = await fetchShiftCloseTickets();
        shiftCloseState.tickets = tickets;
        renderShiftCloseTickets(tickets);
        if (!tickets.length && candidates.length) {
          setShiftCloseStatus('No open tickets assigned to you. You can close your shift now.', 'info');
        }
      } catch (err) {
        setShiftCloseStatus(err && err.message ? err.message : 'Failed to load open tickets.', 'error');
        shiftCloseState.tickets = [];
        renderShiftCloseTickets([]);
      }

      if (shiftCloseSubmit) shiftCloseSubmit.disabled = !candidates.length;
      if (shiftCloseNextUser && !shiftCloseNextUser.disabled) {
        setTimeout(() => {
          try { shiftCloseNextUser.focus(); } catch {}
        }, 60);
      }
    }

    function closeShiftDialog() {
      if (!shiftCloseDlg) return;
      if (typeof shiftCloseDlg.close === 'function') {
        shiftCloseDlg.close();
      } else {
        shiftCloseDlg.open = false;
      }
      shiftCloseState.tickets = [];
      if (shiftCloseNotes) shiftCloseNotes.value = '';
      setShiftCloseStatus('');
      renderShiftCloseTickets([]);
    }

    async function submitShiftClose(event) {
      if (event) event.preventDefault();
      if (shiftCloseState.submitting) return;
      if (!shiftCloseNextUser || shiftCloseNextUser.disabled || !shiftCloseNextUser.value) {
        setShiftCloseStatus('Select the teammate taking over your shift.', 'error');
        return;
      }
      const nextId = Number.parseInt(shiftCloseNextUser.value, 10);
      if (!Number.isFinite(nextId)) {
        setShiftCloseStatus('Select the teammate taking over your shift.', 'error');
        return;
      }
      const notes = shiftCloseNotes ? shiftCloseNotes.value.trim() : '';
      const ticketIds = Array.isArray(shiftCloseState.tickets)
        ? shiftCloseState.tickets.map((ticket) => ticket.id)
        : [];

      shiftCloseState.submitting = true;
      if (shiftCloseSubmit) shiftCloseSubmit.disabled = true;
      setShiftCloseStatus('Submitting handover…', 'info');

      try {
        const payload = {
          next_shift_user_id: nextId,
          handover_ticket_ids: ticketIds,
          notes: notes || null,
        };
        const response = await adminAPI.fetch(API.adminShiftLogout, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        showFlash('Shift closed and handover recorded.');
        shiftPromptDismissed = true;
        closeShiftDialog();
        updateShiftBanner(response);
        reloadTickets({ keepSelection: false }).catch(() => {});
        // If this close was initiated from the logout flow, complete the
        // logout now (shift guard would otherwise re-prompt). The shift is
        // already closed, so skip the guard.
        if (shiftCloseState.logoutAfter) {
          shiftCloseState.logoutAfter = false;
          performLogout({ skipShiftGuard: true, reason: 'shift_handover_complete' });
        }
      } catch (err) {
        setShiftCloseStatus(err && err.message ? err.message : 'Failed to close shift.', 'error');
      } finally {
        shiftCloseState.submitting = false;
        if (shiftCloseSubmit) shiftCloseSubmit.disabled = false;
      }
    }

    async function ensureShiftPrompt(options = {}) {
      const { force = false } = options;
      if (!shiftSetupDlg) {
        // eslint-disable-next-line no-console
        console.warn('shift-management: #shiftSetupDlg missing — shift prompt will not open.');
        return;
      }
      const isShiftEligible = isShiftRegistrant();
      if (!isShiftEligible) return;
      if (!canAccessTicketing()) return;
      if (!force) {
        if (shiftPromptDismissed) return;
      }

      let shiftData = null;
      let reason = 'missing_trunk';
      try {
        shiftData = await adminAPI.fetch(API.adminShiftToday, { method: 'GET' });
      } catch (err) {
        if (err && err.status === 404) {
          shiftData = null;
          reason = 'no_shift';
          updateShiftBanner(null);
        } else {
          console.warn('shift_status_check_failed', err);
          if (force) {
            showFlash(err && err.message ? err.message : 'Failed to load shift information.', 'error');
          }
          return;
        }
      }

      let assignedLines = normalizeStringList(shiftData && shiftData.assigned_lines);
      let pbxTrunks = normalizeStringList(shiftData && shiftData.pbx_trunks);
      const hasLogin = Boolean(shiftData && shiftData.login_at);
      updateShiftBanner(hasLogin ? shiftData : null);

      if (!shiftData || !hasLogin) {
        reason = 'no_shift';
      } else if (!pbxTrunks.length) {
        reason = 'missing_trunk';
      } else if (!force) {
        return;
      }

      let optionsData = null;
      try {
        optionsData = await loadShiftOptions(force);
      } catch (err) {
        if (force) {
          showFlash(err && err.message ? err.message : 'Failed to load PBX staffing options.', 'error');
        } else {
          console.warn('shift_options_load_failed', err);
        }
      }

      const availableTrunks = optionsData ? optionsData.available_trunks : [];
      const defaultLines = getDefaultAssignedLinesForCurrentUser();
      if (defaultLines.length) {
        assignedLines = [...defaultLines];
      }
      if (!pbxTrunks.length && !force && availableTrunks.length === 1) {
        pbxTrunks = [availableTrunks[0]];
      }

      if (shiftSetupDlg.open) {
        openShiftDialog({ assignedLines, pbxTrunks, defaultLines, availableTrunks, reason });
        return;
      }

      shiftPromptDismissed = false;
      openShiftDialog({ assignedLines, pbxTrunks, defaultLines, availableTrunks, reason });
    }

  let mounted = false;
  function mount() {
    if (mounted) return;
    mounted = true;
    resolveDom();
    adminUsersCache = getAdminUsersCache();
    if (shiftLinesInput) {
      shiftLinesInput.readOnly = true;
      shiftLinesInput.title = 'Assigned lines are set automatically based on your role.';
    }

    if (shiftSetupSkip) {
      shiftSetupSkip.addEventListener('click', () => {
        if (!canSkipShiftPrompt()) {
          setShiftStatus('Customer care agents must confirm their shift before proceeding.', 'error');
          return;
        }
        shiftPromptDismissed = true;
      });
    }

    if (shiftSetupBtn) {
      shiftSetupBtn.addEventListener('click', () => {
        shiftPromptDismissed = false;
        ensureShiftPrompt({ force: true }).catch((err) => {
          if (err && err.message) {
            showFlash(err.message, 'error');
          }
        });
      });
    }

    if (shiftCloseBtn) {
      shiftCloseBtn.addEventListener('click', () => {
        openShiftCloseDialog().catch((err) => {
          if (err && err.message) {
            showFlash(err.message, 'error');
          }
        });
      });
    }

    if (shiftCloseCancel) {
      shiftCloseCancel.addEventListener('click', () => closeShiftDialog());
    }

    if (shiftCloseForm) {
      shiftCloseForm.addEventListener('submit', submitShiftClose);
    }

    if (shiftCloseDlg) {
      shiftCloseDlg.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeShiftDialog();
      });
    }

    // shiftSetupDlg cancel/close were originally wired in admin.js at the
    // generic dialog-handling block (lines 3080-3090). Move them here so the
    // module owns the lifecycle.
    if (shiftSetupDlg) {
      shiftSetupDlg.addEventListener('cancel', (event) => {
        event.preventDefault();
        shiftPromptDismissed = true;
        try { shiftSetupDlg.close(); }
        catch { shiftSetupDlg.open = false; }
      });
      shiftSetupDlg.addEventListener('close', () => {
        setShiftStatus('', 'info');
      });
    }

    if (shiftCloseNextUser) {
      shiftCloseNextUser.addEventListener('change', () => {
        if (shiftCloseSubmit) {
          shiftCloseSubmit.disabled = !shiftCloseNextUser.value;
        }
      });
    }

    if (shiftEndAlertConfirm) {
      shiftEndAlertConfirm.addEventListener('click', () => {
        acknowledgeShiftEndAlert();
      });
    }

    if (shiftSetupForm) {
      shiftSetupForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (!shiftSetupDlg) return;
        const assigned = normalizeStringList(parseListInput(shiftLinesInput ? shiftLinesInput.value : '', { lowercase: false }));
        const trunks = normalizeStringList(shiftTrunksSelect
          ? Array.from(shiftTrunksSelect.selectedOptions).map((opt) => opt.value)
          : []);
        if (!trunks.length) {
          setShiftStatus('Select at least one PBX trunk.', 'error');
          if (shiftTrunksSelect) {
            try { shiftTrunksSelect.focus(); } catch {}
          }
          return;
        }
        if (shiftSetupSubmit) shiftSetupSubmit.disabled = true;
        if (shiftSetupSkip) shiftSetupSkip.disabled = true;
        setShiftStatus('Saving…', 'info');
        try {
          const savedShift = await adminAPI.fetch(API.adminShiftLogin, {
            method: 'POST',
            body: JSON.stringify({ assigned_lines: assigned, pbx_trunks: trunks }),
          });
          if (savedShift && savedShift.login_at) {
            updateShiftBanner(savedShift);
          }
          if (shiftLinesInput) shiftLinesInput.value = assigned.join(', ');
          populateTrunkSelect(shiftAvailableTrunks, trunks);
          setShiftStatus('Shift updated.', 'success');
          showFlash('Shift updated');
          shiftPromptDismissed = true;
          setTimeout(() => {
            try { shiftSetupDlg.close(); }
            catch { shiftSetupDlg.open = false; }
          }, 400);
        } catch (err) {
          setShiftStatus(err && err.message ? err.message : 'Failed to save shift.', 'error');
        } finally {
          if (shiftSetupSubmit) shiftSetupSubmit.disabled = false;
          if (shiftSetupSkip) shiftSetupSkip.disabled = false;
        }
      });
    }
  }

  export default {
    mount,
    refreshStatus: refreshShiftStatus,
    startPolling: startShiftStatusPolling,
    stopPolling: stopShiftStatusPolling,
    ensurePrompt: ensureShiftPrompt,
    openSetupDialog: (opts) => openShiftDialog(opts),
    openCloseDialog: openShiftCloseDialog,
    closeDialog: closeShiftDialog,
    clearEndAlert: clearShiftEndAlert,
    evaluateEndAlert: evaluateShiftEndAlert,
    updateBanner: updateShiftBanner,
    updateControls: updateShiftControls,
    isRegistrant: isShiftRegistrant,
    canSkipPrompt: canSkipShiftPrompt,
    getCurrentShift: () => currentShiftInfo,
    setCurrentShift: (info) => { currentShiftInfo = info; },
    getPromptDismissed: () => shiftPromptDismissed,
    setPromptDismissed: (v) => { shiftPromptDismissed = v; },
    isAutoClosing: () => shiftEndAutoClosing,
    setAutoClosing: (v) => { shiftEndAutoClosing = v; },
    getOptionsCache: () => shiftOptionsCache,
    loadShiftOptions,
    populateTrunkSelect,
  };
